import Vue from 'vue'
import _ from 'lodash'
import cookie from '@/common/utils/cookie'
import constant from '@/common/resources/constant'
import { ADULT_LAYER_POPUP } from '@/common/resources/const'
import { PATH_NAME_ADULT, PATH_NAME_PINCODE_CHECK } from '@/common/resources/router.const'
import { getAdultCertificationStatus, getUser } from '@/common/utils/request'
import { getReferer } from '@/common/utils/referer'

export const EventBus = new Vue()

/**
 * 쿠키를 참조하여 성인 인증 여부를 반환한다.
 * @returns {boolean}
 */
export const getCheckAdult = () => {
  return String(cookie.getItem('verifiedAdult')) === '1'
}

/**
 * target age 를 받아 성인 콘텐츠 여부를 리턴한다.
 * @param targetAge
 * @returns {boolean}
 */
export const isAdultContent = (targetAge = '0') => Number(targetAge) > 17

/**
 * content 상세 데이터를 받아 성인 인증 여부를 반환한다.
 * @param contentsData
 * @returns {Promise<{isAdultVerified: boolean}>}
 */
export const checkAdultContent = async (contentsData = {}) => {
  const { targetage: targetAge = '0' } = contentsData
  const isNeedsToCheckAdult = isAdultContent(targetAge) && !isAlreadyVerifiedAdult()

  if (isAlreadyVerifiedAdult()) return { isAdultVerified: true }
  if (isNeedsToCheckAdult) {
    try {
      const { isAdultVerified } = await isAdultVerification()
      return { isAdultVerified }
    } catch (e) {
      console.error(e)
    }
  }

  EventBus.$emit(ADULT_LAYER_POPUP, { isOpen: false })
  return { isAdultVerified: true }
}

/**
 * 성인 인증 여부를 리턴한다. (쿠키 참조)
 * @returns {boolean}
 */
export const isAlreadyVerifiedAdult = () => cookie.hasCredential() && cookie.isVerifiedAdultUser()

/**
 * key{'adult-settings'} value {'N' | 'H' | 'L' |} 를 쿠키에 저장한다.
 * (key 는 항상 adult-settings 인데 인자로 받을 필요가 있나? 레거시 코드라 확인 후 수정 필요해 보임)
 * @param key
 * @param value
 */
export function setCookieAdultSetting (key, value) {
  cookie.setItem(key, value, null, constant.URL_COOKIE_DOMAIN, '/')
}

/**
 * 쿠키에 성인인증 값을 저장한다.
 */
export function setCookieVerifiedAdult () {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  cookie.setItem('verifiedAdult', '1', date, constant.URL_COOKIE_DOMAIN, '/')
}

/**
 * 성인 세팅을 쿠키에서 조회/세팅 후 반환한다.
 * @returns {Promise<*>}
 */
const getCookieAdultSettings = async () => {
  try {
    const { data } = await getAdultCertificationStatus()
    const settings = _.get(data, 'settings', 'N')
    setCookieAdultSetting('adult-settings', settings)

    return settings
  } catch (err) { console.error(err) }
}

/**
 * 레퍼러와 함께 로그인 페이지로 이동한다.
 * @param referer
 */
export const goLoginPageWithReferer = (referer = '/index') => {
  const MEMBER_LOGIN_PAGE = '/member/login'
  location.replace(`${MEMBER_LOGIN_PAGE}?referer=${referer}`)
}

/**
 * Pin Code 입력 페이지로 이동한다. (replace 로 이동 - 다운로드 아닌 경우)
 */
export const replacePinCodePage = () => {
  const { pinCodeUrl = '/setting/pincode_check?type=adult' } = getRefererEnterPinCode()
  return location.replace(pinCodeUrl)
}

/**
 * Pin Code 입력 페이지로 이동한다. (push 로 이동 - 다운로드인 경우 - 브라우저 뒤로가기 이슈 대응)
 */
export function pushPinCodePage (queryRefererUrl) {
  const { enterUrl } = getRefererEnterPinCode()

  return this.$router.push({
    name: PATH_NAME_PINCODE_CHECK,
    query: {
      type: 'adult',
      settings: cookie.getAdultSettings(),
      referer: queryRefererUrl,
      enter: enterUrl
    }
  })
}

/**
 * 성인 풀 페이지 팝업을 연다.
 * @returns {Vue | object | Record<never, any>}
 */
export const openAdultLayerPopup = () => {
  return EventBus.$emit(ADULT_LAYER_POPUP, { isOpen: true })
}

/**
 * 성인 풀 페이지로 이동한다. (다운로드 일 경우 브라우저 뒤로가기 이슈 대응)
 */
export function pushAdultLayerPopupPage () {
  return this.$router.push({ name: PATH_NAME_ADULT, query: { download: 'true' } })
}

/**
 * 쿠키 settings 를 참조하여 잠금 'L' 인 경우를 반환한다
 * @returns {Promise<boolean>}
 */
export const isAdultLocked = async () => {
  /*
    * Adult Setting Index
    * 'N' for None (default)
    * 'L' for Lock
    * 'H' for Hide
    * */
  const settings = await getCookieAdultSettings()
  return settings === 'L'
}

/**
 * 성인 세팅이 잠금('L') 일 경우 Pin Code 페이지를, 아닐경우 성인 인증 풀 팝업을 호출한다.
 * @returns {Promise<void>}
 */
export const commonAdultVerifyProcess = async () => {
  return await isAdultLocked()
    ? replacePinCodePage()
    : openAdultLayerPopup()
}

/**
 * 성인 세팅이 잠금('L') 일 경우 Pin Code 페이지로, 아닐경우 성인 인증 페이지로 이동한다.
 * (다운로드용 페이지 이동은 라우터에 쌓여야 하기 때문에 download 용 함수 사용)
 * @param referer
 * @returns {Promise<*>}
 */
export async function downloadAdultVerifyProcess (referer) {
  return await isAdultLocked()
    ? pushPinCodePage.call(this, referer)
    : pushAdultLayerPopupPage.call(this)
}

export const getRefererEnterPinCode = () => {
  const refererUrl = encodeURIComponent(getReferer()) ?? '/'
  const enterUrl = encodeURIComponent(location.pathname + location.search ?? '/')
  const pinCodeUrl = `/setting/pincode_check?type=adult&settings=${cookie.getAdultSettings()}&referer=${refererUrl}&enter=${enterUrl}`

  return { refererUrl, enterUrl, pinCodeUrl }
}

/**
 * 성인 인증 프로세스 함수 반환. Early Return: 로그인이 되어 있지 않을 시 로그인 페이지로 이동
 * @returns {Promise<void>|void}
 */
export const goVerifyAdultPage = () => {
  const { refererUrl } = getRefererEnterPinCode()
  if (!cookie.hasCredential()) return goLoginPageWithReferer(refererUrl)

  return commonAdultVerifyProcess()
}

/**
 * today 와 만료날짜를 비교 1년 미만 여부와 만료 날짜를 반환한다.
 * @param adultVerification
 * @returns {{nextYearDate: Date, isLessThenYear: boolean}}
 */
export const isCheckNextYearDateAndToday = (adultVerification) => {
  const today = new Date()
  const year = adultVerification.slice(0, 4)
  const month = Number(adultVerification.slice(5, 7)) - 1
  const day = adultVerification.slice(8, 10)
  const oneYearLater = new Date(Number(year) + 1, month, Number(day))
  return { isLessThenYear: today <= oneYearLater, nextYearDate: oneYearLater }
}

/**
 * 성인 인증 여부와 기한 (expire) 을 반환 한다.
 * 유저에 대한 응답값을 만료날짜 계산 후 반환 한다.
 * @returns {Promise<{isAdultVerified: boolean}|{expire: string, isAdultVerified: boolean}>}
 */
export const userAdultVerifiedInfo = async () => {
  if (!cookie.hasCredential()) return { isAdultVerified: false }
  const userRes = await getUser()
  const userData = _.get(userRes, 'data', {})
  const adultVerification = _.get(userData, 'adultverification', '')
  const { isLessThenYear = false, nextYearDate = '' } = isCheckNextYearDateAndToday(adultVerification)
  return { isAdultVerified: isLessThenYear && cookie.getAdultSettings() !== 'L', expire: nextYearDate }
}

/**
 * expire 를 받아 쿠키에 성인 인증을 저장한다. (value 가 '1' 일 경우 성인 인증 완료 됨)
 * @param expire
 */
export const setCookieItemVerified = (expire = '1970-01-01T00:00:00.000Z') => {
  cookie.setItem('verifiedAdult', '1', expire, constant.URL_COOKIE_DOMAIN, '/')
}

/**
 * 필요 시 (성인 인증이 되지 않았을 경우) 성인 인증 팝업을 호출한다.
 * @param isAdultVerified
 * @returns {Promise<void>}
 */
export const openAdultPopupIfNeeded = ({ isAdultVerified = false }) => {
  if (!isAdultVerified) return goVerifyAdultPage()
}

/**
 * 성인 인증 여부와 기한을 받아 성인인증 일 경우 쿠키에 저장한다. 인증 여부를 반환한다. (기존 함수명: doActionAdultVerification)
 * @returns {Promise<{isAdultVerified: boolean}>}
 */
export const isAdultVerification = async () => {
  const { isAdultVerified, expire } = await userAdultVerifiedInfo()
  if (isAdultVerified) setCookieItemVerified(expire)

  return { isAdultVerified }
}
